import qs from 'qs';
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
    GetSavedSearchesWithItemsApiResponse,
    SavedsearchesitemsRequest,
    RemoveSavedSearchesApiResponse,
    RemoveSavedSearchesApiRequest,
    SavedSearchEmailNotificationApiRequest,
    SavedSearchEmailNotificationApiResponse,
} from 'tradera/state/services/types/webapi-discover-generated';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';
import { isServer } from 'tradera/utils/nextjs';

enum Tags {
    SAVED_SEARCHES_LIST = 'SAVED_SEARCHES_LIST',
}

// This was previously a variable that would keep track of the latest cacheKey.
// With that we would only update the latest cache key, so if the user goes
// from page 1 to page 2, and back to page 1, the cache would not be updated for page
// 1 since page 2 would be the latest cache key.
const cacheKeySet = new Set<SavedsearchesitemsRequest>();
export const savedSearchesApi = createApi({
    reducerPath: 'savedSearchesApi',
    baseQuery: isomorphicWebApiBaseQuery,
    tagTypes: Object.keys(Tags),
    endpoints: (builder) => ({
        getSavedSearches: builder.query<
            GetSavedSearchesWithItemsApiResponse,
            SavedsearchesitemsRequest
        >({
            query: (params) => {
                cacheKeySet.add(params);
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/savedsearchesitems?${qs.stringify({
                    ...params,
                    sortBy: 'Alphabetical',
                })}`;
            },
            providesTags: [Tags.SAVED_SEARCHES_LIST],
        }),

        removeSavedSearches: builder.mutation<
            RemoveSavedSearchesApiResponse,
            RemoveSavedSearchesApiRequest
        >({
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/discover/removesavedsearches`,
                    method: 'DELETE',
                    body: params,
                };
            },
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                for (const cacheKey of cacheKeySet) {
                    const patchResult = dispatch(
                        savedSearchesApi.util.updateQueryData(
                            'getSavedSearches',
                            cacheKey,
                            (draft) => {
                                // eslint-disable-next-line better-mutation/no-mutation
                                draft.savedSearches =
                                    draft.savedSearches?.filter(
                                        (item) =>
                                            !params?.ids?.includes(item.id),
                                    );
                            },
                        ),
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patchResult.undo();
                    }
                }
            },
            invalidatesTags: [Tags.SAVED_SEARCHES_LIST],
        }),

        toggleEmailNotification: builder.mutation<
            SavedSearchEmailNotificationApiResponse,
            SavedSearchEmailNotificationApiRequest
        >({
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/discover/savedsearchemailnotification`,
                    method: 'POST',
                    body: { ...params },
                };
            },
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                for (const cacheKey of cacheKeySet) {
                    const patchResult = dispatch(
                        savedSearchesApi.util.updateQueryData(
                            'getSavedSearches',
                            cacheKey,
                            (draft) => {
                                draft.savedSearches?.forEach((item) => {
                                    if (item.id === params.savedSearchDto?.id) {
                                        Object.assign(item, {
                                            hasEnabledEmailNotifications:
                                                params.savedSearchDto.sendMail,
                                        });
                                    }
                                });
                            },
                        ),
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patchResult.undo();
                    }
                }
            },
        }),
    }),
});

export const {
    useGetSavedSearchesQuery,
    useRemoveSavedSearchesMutation,
    useToggleEmailNotificationMutation,
} = savedSearchesApi;
